import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";

// Pages
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import StudentDashboard from "@/pages/StudentDashboard";
import TeacherDashboard from "@/pages/TeacherDashboard";
import LibrarianDashboard from "@/pages/LibrarianDashboard";
import ParentDashboard from "@/pages/ParentDashboard";
import BookSearch from "@/pages/BookSearch";
import BookDetails from "@/pages/BookDetails";

// Staff Dashboard
import { StaffDashboard } from "@/components/dashboard/StaffDashboard";

// TISA Coins Pages
import StudentCoinsPage from "@/pages/TISACoins/StudentCoinsPage";
import TeacherCoinsPage from "@/pages/TISACoins/TeacherCoinsPage";

// TISA BizBook (Student Profile)
import BizBookPage from "@/pages/student/BizBookPage";

// Layouts and Guards
import RoleLayout from "@/components/layouts/RoleLayout";

// Error Boundary
import ErrorBoundary from "@/components/common/ErrorBoundary";

// Auth Provider (real Supabase auth)
import { AuthProvider } from "@/components/auth/AuthProvider";

// Global styles
import "@/styles/globals.css";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error?.message?.includes("4")) return false;
        return failureCount < 2;
      },
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider
            attribute="class"
            defaultTheme="light"
            enableSystem
            disableTransitionOnChange
          >
            <Router>
              <div className="min-h-screen bg-background font-child-friendly">
                {/* Skip to main content for accessibility */}
                <a href="#main-content" className="skip-link">
                  Skip to main content
                </a>

                <Routes>
                  {/* Temporary: Direct access without authentication */}
                  <Route path="/login" element={<LoginPage />} />

                  {/* Direct student routes (no auth for demo) */}
                  <Route
                    path="/student/*"
                    element={
                      <RoleLayout role="student">
                        <Routes>
                          <Route
                            path="dashboard"
                            element={<StudentDashboard />}
                          />
                          <Route path="coins" element={<StudentCoinsPage />} />
                          <Route path="bizbook" element={<BizBookPage />} />
                          <Route
                            path="legend"
                            element={<Navigate to="/student/bizbook" replace />}
                          />
                          <Route
                            path="profile"
                            element={<Navigate to="/student/bizbook" replace />}
                          />
                          <Route path="books" element={<BookSearch />} />
                          <Route path="books/:id" element={<BookDetails />} />
                          <Route
                            path="*"
                            element={
                              <Navigate to="/student/dashboard" replace />
                            }
                          />
                        </Routes>
                      </RoleLayout>
                    }
                  />

                  {/* Staff routes */}
                  <Route
                    path="/staff/*"
                    element={
                      <RoleLayout role="teacher">
                        <Routes>
                          <Route
                            path="dashboard"
                            element={<StaffDashboard />}
                          />
                          <Route
                            path="students"
                            element={<div>Student Management Coming Soon</div>}
                          />
                          <Route
                            path="students/add"
                            element={<div>Add Student Coming Soon</div>}
                          />
                          <Route path="books" element={<BookSearch />} />
                          <Route
                            path="books/add"
                            element={<div>Add Book Manual Coming Soon</div>}
                          />
                          <Route
                            path="books/scan"
                            element={<div>Scan Book Coming Soon</div>}
                          />
                          <Route
                            path="*"
                            element={<Navigate to="/staff/dashboard" replace />}
                          />
                        </Routes>
                      </RoleLayout>
                    }
                  />

                  <Route
                    path="/teacher/*"
                    element={
                      <RoleLayout role="teacher">
                        <Routes>
                          <Route
                            path="dashboard"
                            element={<TeacherDashboard />}
                          />
                          <Route path="coins" element={<TeacherCoinsPage />} />
                          <Route
                            path="class/:class"
                            element={<TeacherDashboard />}
                          />
                          <Route path="books" element={<BookSearch />} />
                          <Route path="books/:id" element={<BookDetails />} />
                          <Route
                            path="*"
                            element={
                              <Navigate to="/teacher/dashboard" replace />
                            }
                          />
                        </Routes>
                      </RoleLayout>
                    }
                  />

                  <Route
                    path="/librarian/*"
                    element={
                      <RoleLayout role="librarian">
                        <Routes>
                          <Route
                            path="dashboard"
                            element={<LibrarianDashboard />}
                          />
                          <Route path="books" element={<BookSearch />} />
                          <Route path="books/:id" element={<BookDetails />} />
                          <Route
                            path="reports"
                            element={<LibrarianDashboard />}
                          />
                          <Route
                            path="settings"
                            element={<LibrarianDashboard />}
                          />
                          <Route
                            path="*"
                            element={
                              <Navigate to="/librarian/dashboard" replace />
                            }
                          />
                        </Routes>
                      </RoleLayout>
                    }
                  />

                  <Route
                    path="/parent/*"
                    element={
                      <RoleLayout role="parent">
                        <Routes>
                          <Route
                            path="dashboard"
                            element={<ParentDashboard />}
                          />
                          <Route
                            path="child/:id"
                            element={<StudentDashboard />}
                          />
                          <Route
                            path="*"
                            element={
                              <Navigate to="/parent/dashboard" replace />
                            }
                          />
                        </Routes>
                      </RoleLayout>
                    }
                  />

                  {/* Global book routes (open access for demo) */}
                  <Route
                    path="/books/*"
                    element={
                      <Routes>
                        <Route path="search" element={<BookSearch />} />
                        <Route path=":id" element={<BookDetails />} />
                        <Route
                          path="*"
                          element={<Navigate to="/books/search" replace />}
                        />
                      </Routes>
                    }
                  />

                  {/* Root shows home page */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>

                {/* Global toast notifications */}
                <Toaster />
              </div>
            </Router>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
